import type { AnswerBody, ChangeBody } from '@/shared/api/generated/model';

/**
 * Выбор изменений для переноса схемы.
 *
 * Зависимости — с сервера (`requires`): тип раньше своих полей, группа раньше
 * её полей, блок раньше поля, которое его разрешает. Отметили поле — его
 * зависимости отмечаются сами; сняли тип — снимается всё, что без него не
 * перенести. Иначе человек получал бы отказ сервера и искал бы глазами,
 * чего не хватает.
 */
export function toggle(changes: readonly ChangeBody[], selected: ReadonlySet<string>, id: string) {
  const next = new Set(selected);

  if (next.has(id)) {
    // Снимаем вместе со всем, что от него зависит, — по цепочке.
    const queue = [id];

    while (queue.length > 0) {
      const current = queue.pop() ?? '';

      next.delete(current);

      for (const change of changes) {
        if (next.has(change.id) && (change.requires ?? []).includes(current)) {
          queue.push(change.id);
        }
      }
    }

    return next;
  }

  const queue = [id];

  while (queue.length > 0) {
    const current = queue.pop() ?? '';

    if (next.has(current)) {
      continue;
    }

    next.add(current);

    for (const dep of changes.find((change) => change.id === current)?.requires ?? []) {
      queue.push(dep);
    }
  }

  return next;
}

/** Ответ на вопрос изменения. */
export interface Answer {
  /** Значение по умолчанию для пустых записей. */
  value?: unknown;
  /** Согласие с последствиями (или оставить пустые пустыми). */
  confirm: boolean;
}

/** Изменения, которые отмечены, но без ответа на свой вопрос. */
export function unanswered(
  changes: readonly ChangeBody[],
  selected: ReadonlySet<string>,
  answers: Readonly<Record<string, Answer>>,
): ChangeBody[] {
  return changes.filter((change) => {
    if (!selected.has(change.id) || change.question === 'none') {
      return false;
    }

    const answer = answers[change.id];

    if (answer === undefined) {
      return true;
    }

    if (change.question === 'default') {
      return !answer.confirm && isBlank(answer.value);
    }

    return !answer.confirm;
  });
}

/** Ответы в том виде, в каком их ждёт сервер. */
export function answersFor(
  changes: readonly ChangeBody[],
  selected: ReadonlySet<string>,
  answers: Readonly<Record<string, Answer>>,
): AnswerBody[] {
  return changes.flatMap((change): AnswerBody[] => {
    const answer = answers[change.id];

    if (!selected.has(change.id) || change.question === 'none' || answer === undefined) {
      return [];
    }

    if (change.question === 'default' && !isBlank(answer.value)) {
      return [{ change_id: change.id, default: answer.value }];
    }

    return [{ change_id: change.id, confirm: answer.confirm }];
  });
}

export interface TypeGroup {
  typeID: string;
  name: string;
  kind: ChangeBody['type_kind'];
  changes: ChangeBody[];
}

/** Изменения по типам: тип и его поля — рядом, в порядке сервера. */
export function byType(changes: readonly ChangeBody[]): TypeGroup[] {
  const groups: TypeGroup[] = [];

  for (const change of changes) {
    let group = groups.find((item) => item.typeID === change.type_id);

    if (group === undefined) {
      group = {
        typeID: change.type_id,
        name: change.type_name,
        kind: change.type_kind,
        changes: [],
      };
      groups.push(group);
    }

    group.changes.push(change);
  }

  return groups;
}

const ATTR_TITLES: Record<string, string> = {
  name: 'название',
  slug: 'короткое имя',
  description: 'пояснение',
  icon: 'иконка',
  folder: 'папка в меню',
  seo: 'SEO',
  title_field: 'поле-название',
  route: 'адрес записи',
  nav: 'место в меню',
  manual_order: 'ручной порядок',
  api_access: 'доступ с сайта',
  purpose: 'раздел',
  auth: 'вход посетителей',
  position: 'порядок',
  type: 'вид поля',
  hint: 'подсказка',
  required: 'обязательность',
  options: 'настройки вида',
  widget: 'вид редактора',
  rules: 'проверки',
  show_if: 'условие показа',
  width: 'ширина',
  tab: 'вкладка',
  translatable: 'переводы',
  parent: 'группа',
};

export function attrTitle(key: string): string {
  return ATTR_TITLES[key] ?? key;
}

/**
 * Значение свойства словами. Сложные (проверки, настройки) не
 * расписываются: важно, что они поменялись, а подробности видны в «Моделях».
 */
const NAV_TITLES: Record<string, string> = {
  menu: 'отдельным пунктом',
  lookup: 'в справочниках',
  hidden: 'скрыт',
};

const ACCESS_TITLES: Record<string, string> = {
  public: 'всем',
  none: 'никому',
  signed_in: 'вошедшим',
  own: 'только своё',
};

export function attrValue(key: string, value: unknown): string | undefined {
  if (key === 'api_access' && typeof value === 'object' && value !== null) {
    const access = value as { read?: string; create?: string; update?: string; delete?: string };
    const title = (level?: string) => ACCESS_TITLES[level ?? 'none'] ?? '—';

    return `читать — ${title(access.read)}, создавать — ${title(access.create)}, менять — ${title(access.update)}, удалять — ${title(access.delete)}`;
  }

  if (key === 'auth' && typeof value === 'object' && value !== null) {
    const auth = value as { registration?: string; confirm_email?: boolean };

    return `регистрация ${auth.registration === 'closed' ? 'закрыта' : 'открыта'}, почту ${auth.confirm_email === false ? 'не подтверждать' : 'подтверждать'}`;
  }

  if (key === 'purpose' && typeof value === 'string') {
    return value === 'form' ? '«Формы»' : value === 'users' ? '«Пользователи»' : '«Модели»';
  }

  if (key === 'nav' && typeof value === 'string') {
    return NAV_TITLES[value] ?? value;
  }

  if (key === 'options' || key === 'rules' || key === 'show_if' || key === 'position') {
    return undefined;
  }

  if (typeof value === 'boolean') {
    if (key === 'parent') {
      return value ? 'в группе' : 'вне группы';
    }

    return value ? 'да' : 'нет';
  }

  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (key === 'width') {
    return value === 'half' ? 'половина' : 'вся ширина';
  }

  return typeof value === 'string' ? value : JSON.stringify(value);
}

function isBlank(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
}
